import { useEffect, useMemo, useState } from "react";

type Kind = "mouse" | "touch" | "pen" | null;

function envHasCoarse(): boolean {
  if (typeof window === "undefined") return false;
  const mm = (q: string) => window.matchMedia?.(q).matches ?? false;
  return mm("(pointer: coarse)") || mm("(any-pointer: coarse)") || (navigator as any).maxTouchPoints > 0;
}

/** Tracks whether the environment has any coarse input and what the last active pointer type was. */
export default function usePointerKind() {
  const [hasCoarse, setHasCoarse] = useState(envHasCoarse());
  const [active, setActive] = useState<Kind>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const q1 = window.matchMedia("(pointer: coarse)");
    const q2 = window.matchMedia("(any-pointer: coarse)");
    const update = () => setHasCoarse(envHasCoarse());

    const add = (mql: MediaQueryList) =>
      (mql.addEventListener ? mql.addEventListener("change", update) : mql.addListener(update));
    const rm = (mql: MediaQueryList) =>
      (mql.removeEventListener ? mql.removeEventListener("change", update) : mql.removeListener(update));

    const onPointerDown = (e: PointerEvent) => setActive(e.pointerType as Kind);

    add(q1); add(q2);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      rm(q1); rm(q2);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  const isActiveCoarse = useMemo(
    () => (active ? active !== "mouse" : hasCoarse), // treat pen as “coarse” for hoverless UX
    [active, hasCoarse]
  );

  return { hasCoarse, activePointer: active, isActiveCoarse };
}
